import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { getContractorEarnings, confirmPaymentReceived, unconfirmPaymentReceived } from '../../../services/contractorService';
import { paymentBatchService } from '../../../services/paymentBatchService';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { ContractorJobHierarchy } from '../../../components/contractor/jobs';
import ContractorInvoice from '../../../components/contractor/earnings/ContractorInvoice';
import PaymentDetailsModal from '../../../components/contractor/earnings/PaymentDetailsModal';
import { DollarSign, CheckCircle, Clock, TrendingUp, Search, Calendar, X } from 'lucide-react';
import { useScreenSize } from '../../../hooks/useScreenSize';

// Helper to transform snake_case job data to camelCase for ContractorInvoice component
const transformJobForInvoice = (job, instances = []) => {
  if (!job) return null;

  // Prefer job_site over service_receiver (new data model)
  const jobSite = job.job_site || job.service_receiver;

  return {
    id: job.id,
    jobDisplayId: job.job_display_id || `JOB-${String(job.id).slice(-6).toUpperCase()}`,
    jobName: job.job_name,
    jobType: job.job_type,
    description: job.description,
    priority: job.priority,
    status: job.status,
    locationAddress: job.location_address,
    paymentType: job.payment_type,
    paymentAmount: job.payment_amount,
    contractorIncludeGst: job.contractor_include_gst,
    contractorGstType: job.contractor_gst_type,
    estimatedHours: job.estimated_hours,
    scheduledDate: job.scheduled_date,
    scheduledTime: job.scheduled_time,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    // Job site info (preferred over legacy serviceReceiver)
    jobSite: jobSite ? {
      id: jobSite.id,
      siteName: jobSite.site_name || jobSite.receiver_name,
      address: jobSite.address,
      contactPerson: jobSite.contact_person,
      contactEmail: jobSite.contact_email,
      contactPhone: jobSite.contact_phone
    } : null,
    // Keep serviceReceiver for backward compatibility with ContractorInvoice component
    serviceReceiver: jobSite ? {
      id: jobSite.id,
      receiver_name: jobSite.site_name || jobSite.receiver_name,
      receiver_type: jobSite.receiver_type || 'company',
      contact_person: jobSite.contact_person,
      contact_email: jobSite.contact_email,
      contact_phone: jobSite.contact_phone,
      address: jobSite.address
    } : null,
    recurringSchedule: job.recurring_schedule,
    photosRequiredBefore: job.photos_required_before,
    photosRequiredAfter: job.photos_required_after,
    questionnaireTemplate: job.questionnaire_template,
    safetyRequirements: job.safety_requirements,
    equipmentList: job.equipment_list,
    otherDetails: job.other_details,
    clockInRadius: job.clock_in_radius,
    contactPersonName: job.contact_person_name,
    contactPersonPhone: job.contact_person_phone,
    contactPersonEmail: job.contact_person_email,
    assignedContractor: job.contractor ? {
      id: job.contractor.id,
      fullName: job.contractor.full_name
    } : null,
    // In earnings mode, instances are full timesheet objects with nested job_instance
    // In other modes, instances are job_instance objects directly
    job_instances: instances.map(inst => {
      const jobInstance = inst.job_instance || inst; // Handle both timesheet wrapper and direct instance
      return {
        id: jobInstance.id,
        instance_number: jobInstance.instance_number,
        scheduled_date: jobInstance.scheduled_date,
        scheduled_time: jobInstance.scheduled_time,
        status: jobInstance.status,
        contractor: jobInstance.contractor
      };
    })
  };
};

const transformInstanceForInvoice = (instance) => {
  if (!instance) return null;
  return {
    id: instance.id,
    instance_number: instance.instance_number,
    scheduled_date: instance.scheduled_date,
    scheduled_time: instance.scheduled_time,
    status: instance.status,
    contractor: instance.contractor
  };
};

/**
 * Earnings
 *
 * Displays contractor earnings history using the hierarchical
 * Job Site → Job → Timesheet layout.
 */
const Earnings = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { currentCompany } = useCompany();
  const { isMobile } = useScreenSize();
  const [earningsData, setEarningsData] = useState({
    timesheets: [],
    totalEarnings: '0.00',
    pendingEarnings: '0.00',
    totalJobs: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterPeriod, setFilterPeriod] = useState('month');
  const [weekFilter, setWeekFilter] = useState(null);
  const [searchParams] = useSearchParams();

  // Invoice modal state
  const [viewingJobInvoice, setViewingJobInvoice] = useState(null); // { job, instances }
  const [viewingInstanceInvoice, setViewingInstanceInvoice] = useState(null); // { job, instance }

  // Payment confirmation state
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

  // Transaction search state
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionSearchResults, setTransactionSearchResults] = useState(new Set());

  // Payment details modal state
  const [viewingPaymentProof, setViewingPaymentProof] = useState(null);

  const highlightId = searchParams.get('highlight');

  // Fetch earnings data
  const fetchEarnings = useCallback(async () => {
    if (!profile?.id || !currentCompany?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Calculate date range based on filter
      const now = new Date();
      let startDate = null;

      if (filterPeriod === 'week') {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
      } else if (filterPeriod === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      const filters = startDate ? { startDate: startDate.toISOString() } : {};

      const data = await getContractorEarnings(profile.id, currentCompany.id, filters);
      setEarningsData(data);
    } catch (err) {
      console.error('Error loading earnings:', err);
      setError('Failed to load earnings data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile?.id, currentCompany?.id, filterPeriod]);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  /**
   * Handle payment confirmation toggle
   * @param {string} timesheetId - The timesheet to confirm/unconfirm
   * @param {boolean} shouldConfirm - Whether to confirm or unconfirm
   */
  const handleConfirmPayment = useCallback(async (timesheetId, shouldConfirm) => {
    if (!profile?.id) return;

    setIsConfirmingPayment(true);
    try {
      if (shouldConfirm) {
        await confirmPaymentReceived(timesheetId, profile.id);
      } else {
        await unconfirmPaymentReceived(timesheetId, profile.id);
      }

      // Refetch earnings to ensure UI is synced with database
      await fetchEarnings();
    } catch (err) {
      console.error('Error confirming payment:', err);
    } finally {
      setIsConfirmingPayment(false);
    }
  }, [profile?.id, fetchEarnings]);

  // Search by transaction ID
  useEffect(() => {
    const searchByTransaction = async () => {
      if (!profile?.id || !searchTerm || searchTerm.length < 3) {
        setTransactionSearchResults(new Set());
        return;
      }

      try {
        const timesheetIds = await paymentBatchService.searchByTransactionForContractor(profile.id, searchTerm);
        setTransactionSearchResults(new Set(timesheetIds));
      } catch (err) {
        console.error('Error searching by transaction:', err);
        setTransactionSearchResults(new Set());
      }
    };

    // Debounce the transaction search
    const timeoutId = setTimeout(searchByTransaction, 300);
    return () => clearTimeout(timeoutId);
  }, [profile?.id, searchTerm]);

  // Week of month helper
  const getWeekOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfMonth = date.getDate();
    const dayOfWeek = firstDay.getDay();
    return Math.ceil((dayOfMonth + dayOfWeek) / 7);
  };

  // Filter timesheets by week
  const filterByWeek = (week) => {
    setWeekFilter(week === weekFilter ? null : week);
  };

  // Apply week filter and transaction search
  const filteredTimesheets = (() => {
    let timesheets = earningsData?.timesheets || [];

    // Apply week filter
    if (weekFilter) {
      timesheets = timesheets.filter(t => {
        const date = new Date(t?.created_at);
        return getWeekOfMonth(date) === weekFilter;
      });
    }

    // Apply transaction search filter
    if (searchTerm && searchTerm.length >= 3 && transactionSearchResults.size > 0) {
      timesheets = timesheets.filter(t => transactionSearchResults.has(t.id));
    }

    return timesheets;
  })();

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Header />

        <main className="flex-1 overflow-y-auto overflow-x-hidden pt-16 pl-0 lg:pl-60">
          <div className="max-w-full px-4 py-6 sm:px-6 sm:py-8 sm:container sm:mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <div className="mb-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Earnings</h1>
                <p className="text-text-secondary mt-1">Track your income and completed jobs</p>
              </div>

              {/* Filters Bar */}
              <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-wrap items-center gap-3'}`}>
                {/* Period Filter Pills */}
                <div className={`flex items-center gap-1 p-1 bg-surface border border-border rounded-lg ${isMobile ? 'w-full' : ''}`}>
                  {[
                    { id: 'week', label: 'This Week' },
                    { id: 'month', label: 'This Month' },
                    { id: 'all', label: 'All Time' }
                  ].map(period => (
                    <button
                      key={period.id}
                      onClick={() => {
                        setFilterPeriod(period.id);
                        if (period.id !== 'month') setWeekFilter(null);
                      }}
                      className={`${isMobile ? 'flex-1' : ''} px-3 py-2 sm:py-1.5 rounded-md text-sm font-medium transition-all touch-target-sm ${
                        filterPeriod === period.id
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>

                {/* Week Filter (only when month is selected) */}
                {filterPeriod === 'month' && (
                  <div className={`flex items-center gap-1 p-1 bg-surface border border-border rounded-lg ${isMobile ? 'w-full' : ''}`}>
                    <Calendar size={14} className="text-text-secondary ml-2" />
                    {[1, 2, 3, 4].map(week => (
                      <button
                        key={week}
                        onClick={() => filterByWeek(week)}
                        className={`${isMobile ? 'flex-1' : ''} px-2.5 py-2 sm:py-1.5 rounded-md text-sm font-medium transition-all touch-target-sm ${
                          weekFilter === week
                            ? 'bg-green-500 text-white shadow-sm'
                            : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                        }`}
                      >
                        W{week}
                      </button>
                    ))}
                    {weekFilter && (
                      <button
                        onClick={() => setWeekFilter(null)}
                        className="p-2 sm:p-1.5 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors touch-target-sm"
                        title="Clear week filter"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                )}

                {/* Spacer - only on desktop */}
                {!isMobile && <div className="flex-1" />}

                {/* Transaction Search */}
                <div className={`relative ${isMobile ? 'w-full' : ''}`}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search Transaction ID..."
                    className={`${isMobile ? 'w-full' : 'w-52'} pl-9 pr-3 py-2.5 sm:py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-sm transition-all`}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-secondary hover:text-text-primary rounded transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Analytics Button */}
                <button
                  onClick={() => navigate('/contractor/earnings-tracker')}
                  className={`flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium touch-target-sm ${isMobile ? 'w-full' : ''}`}
                >
                  <TrendingUp size={18} />
                  <span>Analytics</span>
                </button>
              </div>
            </div>

            {/* Compact Summary Bar */}
            <div className="bg-surface border border-border rounded-xl mb-6 overflow-hidden">
              <div className={`grid ${isMobile ? 'grid-cols-1 divide-y' : 'grid-cols-3 divide-x'} divide-border`}>
                {/* Total Earnings */}
                <div className="p-4">
                  <div className={`flex items-center ${isMobile ? 'gap-4' : 'gap-3'}`}>
                    <div className="w-10 h-10 bg-green-500 bg-opacity-20 rounded-lg flex items-center justify-center shrink-0">
                      <DollarSign size={20} className="text-green-500" />
                    </div>
                    <div>
                      <span className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-text-primary`}>${earningsData?.totalEarnings}</span>
                      <p className="text-text-secondary text-sm">Total Earnings</p>
                    </div>
                  </div>
                </div>

                {/* Pending Approval */}
                <div className="p-4">
                  <div className={`flex items-center ${isMobile ? 'gap-4' : 'gap-3'}`}>
                    <div className="w-10 h-10 bg-yellow-500 bg-opacity-20 rounded-lg flex items-center justify-center shrink-0">
                      <Clock size={20} className="text-yellow-500" />
                    </div>
                    <div>
                      <span className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-text-primary`}>${earningsData?.pendingEarnings}</span>
                      <p className="text-text-secondary text-sm">Pending Approval</p>
                    </div>
                  </div>
                </div>

                {/* Total Jobs */}
                <div className="p-4">
                  <div className={`flex items-center ${isMobile ? 'gap-4' : 'gap-3'}`}>
                    <div className="w-10 h-10 bg-orange-500 bg-opacity-20 rounded-lg flex items-center justify-center shrink-0">
                      <CheckCircle size={20} className="text-orange-500" />
                    </div>
                    <div>
                      <span className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-text-primary`}>{earningsData?.totalJobs}</span>
                      <p className="text-text-secondary text-sm">Total Jobs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Earnings History */}
            {!loading && !error && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-text-primary">Earnings History</h3>
                  {filteredTimesheets?.length > 0 && (
                    <span className="text-text-secondary text-sm">
                      {filteredTimesheets.length} {filteredTimesheets.length === 1 ? 'entry' : 'entries'}
                    </span>
                  )}
                </div>
                <ContractorJobHierarchy
                  timesheets={filteredTimesheets || []}
                  mode="earnings"
                  highlightInstanceId={highlightId}
                  onViewJobInvoice={(job, instances) => setViewingJobInvoice({ job, instances })}
                  onViewInstanceInvoice={(job, instance, timesheet) => setViewingInstanceInvoice({ job, instance, timesheet })}
                  onConfirmPayment={handleConfirmPayment}
                  isConfirmingPayment={isConfirmingPayment}
                  onViewPaymentProof={(timesheetId) => setViewingPaymentProof(timesheetId)}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Job Invoice Modal (for job-level view) */}
      {viewingJobInvoice && (() => {
        // For single instance jobs, pass the instance and timesheet data
        const isSingleInstance = viewingJobInvoice.instances?.length === 1;
        const singleInstance = isSingleInstance ? viewingJobInvoice.instances[0] : null;
        // In earnings mode, the instance IS the timesheet object with job_instance nested
        const singleTimesheet = isSingleInstance ? singleInstance : null;
        const actualInstance = singleTimesheet?.job_instance || singleInstance;

        return (
          <ContractorInvoice
            job={transformJobForInvoice(viewingJobInvoice.job, viewingJobInvoice.instances)}
            instance={isSingleInstance ? transformInstanceForInvoice(actualInstance) : null}
            company={currentCompany}
            contractor={profile}
            onClose={() => setViewingJobInvoice(null)}
            timesheet={isSingleInstance ? singleTimesheet : null}
            workNotes={isSingleInstance ? actualInstance?.work_notes : null}
            checklistData={isSingleInstance ? actualInstance?.checklist_data : null}
            questionnaireResponses={isSingleInstance ? actualInstance?.questionnaire_responses : null}
            photos={isSingleInstance ? (actualInstance?.photos || []) : []}
          />
        );
      })()}

      {/* Instance Invoice Modal (for instance-level view) */}
      {viewingInstanceInvoice && (
        <ContractorInvoice
          job={transformJobForInvoice(viewingInstanceInvoice.job, [viewingInstanceInvoice.instance])}
          instance={transformInstanceForInvoice(viewingInstanceInvoice.instance)}
          company={currentCompany}
          contractor={profile}
          onClose={() => setViewingInstanceInvoice(null)}
          timesheet={viewingInstanceInvoice.timesheet}
          workNotes={viewingInstanceInvoice.instance?.work_notes}
          checklistData={viewingInstanceInvoice.instance?.checklist_data}
          questionnaireResponses={viewingInstanceInvoice.instance?.questionnaire_responses}
          photos={viewingInstanceInvoice.instance?.photos || []}
        />
      )}

      {/* Payment Details Modal (View Proof) */}
      {viewingPaymentProof && (
        <PaymentDetailsModal
          timesheetId={viewingPaymentProof}
          onClose={() => setViewingPaymentProof(null)}
        />
      )}
    </div>
  );
};

export default Earnings;
